import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useAuth, formatApiErrorDetail } from '../../auth';
import { useCurrency, useCurrencyBusy } from '../keno/CurrencyContext';
import { useRevealedStats } from '../keno/useRevealedStats';
import { useWalletPresentation } from '../keno/useWalletPresentation';
import { waitForPresentation } from '../keno/waitForPresentation';
import { withWalletTurn } from '../keno/walletTurns';
import { useKenoSound } from '../keno/useKenoSound';
import { useBalanceSync } from '../keno/useBalanceSync';
import { useOriginalsAccess } from '../originals/useOriginalsAccess';
import { readDemoWallet, saveDemoWallet, toMinor } from '../keno/credits';
import { KENO_API as API, gameHeaders, loadFairness, rotateFairness } from '../keno/fairnessApi';
import { BlackjackVerifyForm } from './BlackjackVerifyForm';
import { animateOpeningDeal } from './dealAnimation';
import { useOriginalEffects } from '../originals/useOriginalEffects';

const readSettings = () => { try { return JSON.parse(localStorage.getItem('gv_keno_settings')) || {}; } catch { return {}; } };
export const useBlackjack = () => {
  const { user, loading } = useAuth();
  const access = useOriginalsAccess(user);
  const currency = useCurrency();
  const demo = !user, owner = user ? user.id + (currency.accountDemo ? ':account-demo' : '') : 'demo';
  const { stageRound, revealRound } = useRevealedStats(owner, 'blackjack');
  const { reserveStake, confirmWallet, revealPayout, cancelStake, presentWallet } = useWalletPresentation(owner, 'blackjack');
  const [config, setConfig] = useState(null);
  const [wallet, setWallet] = useState({ balance_minor: 0, vault_minor: 0, revision: 0, rounds: [], ghost: true });
  const walletRef = useRef(wallet);
  const [round, setRound] = useState(null);
  const [dealerShown, setDealerShown] = useState(0);
  const [openingStep, setOpeningStep] = useState(null);
  const [settlementVisible, setSettlementVisible] = useState(true);
  const [fairness, setFairness] = useState(null);
  const [walletLoading, setWalletLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false), epoch = useRef(0), visibleRoundId = useRef(null);
  const turnPending = useRef(false);
  const [error, setError] = useState('');
  const [bet, setBet] = useState('1.00');
  const [pairBet, setPairBet] = useState('0.00');
  const [trioBet, setTrioBet] = useState('0.00');
  const [mode, setMode] = useState('standard');
  const [instant, setInstant] = useState(() => !!readSettings().instant);
  const [turbo, setTurbo] = useState(() => !!readSettings().turbo);
  useEffect(() => {
    let cancelled = false;
    if (round?.finished && settlementVisible && openingStep === null) {
      waitForPresentation('[data-testid="blackjack-table"]', instant).then(() => {
        if (!cancelled) { revealPayout(round); revealRound(round); }
      });
    }
    return () => { cancelled = true; };
  }, [round, settlementVisible, openingStep, revealRound, revealPayout, instant]);
  const pace = turbo ? .35 : 1;
  const [muted, setMuted] = useState(() => !!readSettings().muted);
  const [volume, setVolume] = useState(() => Number.isFinite(readSettings().volume) ? readSettings().volume : 60);
  const [ghostBusy, setGhostBusy] = useState(false);
  const sound = useKenoSound(muted, volume);
  const effects = useOriginalEffects(muted, volume);
  const active = !!round && !round.finished;
  const applyWallet = useCallback((value) => { walletRef.current = value; setWallet(value); }, []);
  useBalanceSync({ owner, demo, walletRef, apply: applyWallet, isBusy: () => busyRef.current });
  useEffect(() => { localStorage.setItem('gv_keno_settings', JSON.stringify({ instant, turbo, muted, volume })); }, [instant, turbo, muted, volume]);
  useEffect(() => { if (loading) return; let alive = true; axios.get(`${API}/blackjack/config`, { headers: gameHeaders(demo) }).then(({ data }) => { if (alive) setConfig(data); }).catch(() => { if (alive) setError('Unable to load Blackjack.'); }); return () => { alive = false; }; }, [loading, demo, owner]);
  const refresh = async (version = epoch.current) => {
    await loadFairness(demo).then((data) => { if (version === epoch.current) setFairness(data); });
    const [balance, state] = await Promise.all([
      demo ? Promise.resolve(readDemoWallet()) : axios.get(`${API}/keno/wallet`, { headers: gameHeaders(false) }).then((r) => r.data),
      axios.get(`${API}/blackjack/${demo ? 'demo/' : ''}state`, { headers: gameHeaders(demo) }).then((r) => r.data),
    ]);
    if (version !== epoch.current || busyRef.current) return;
    if (state.round && !state.round.finished) visibleRoundId.current = state.round.request_id;
    const shown = state.round?.finished && state.round.request_id !== visibleRoundId.current ? null : state.round;
    applyWallet(balance); setRound(shown); setDealerShown(shown?.dealer_cards.length || 0); setSettlementVisible(true); setWalletLoading(false);
  };
  useEffect(() => {
    const version = ++epoch.current;
    busyRef.current = false; visibleRoundId.current = null; setBusy(false); setRound(null); setOpeningStep(null); setFairness(null); setError(''); setWalletLoading(true);
    if (loading) return;
    const load = () => { if (!busyRef.current) refresh(version).catch(() => { if (version === epoch.current) { setError('Unable to restore your game. Please try again.'); setWalletLoading(false); } }); };
    load(); const timer = setInterval(load, 5000);
    return () => { epoch.current = version + 1; clearInterval(timer); };
  }, [owner, loading]); // eslint-disable-line react-hooks/exhaustive-deps
  const send = async (action) => {
    if (turnPending.current) return;
    turnPending.current = true;
    const current = epoch.current;
    try { return await withWalletTurn(owner, () => current === epoch.current ? sendNow(action) : null); }
    finally { turnPending.current = false; }
  };
  const sendNow = async (action) => {
    if (action === 'deal' && (access.blocked || access.loading || access.error)) return;
    if (busyRef.current || !fairness || !config || loading || walletLoading) return;
    const version = epoch.current;
    busyRef.current = true; setBusy(true); setError('');
    if (demo) applyWallet(readDemoWallet());
    const payload = { request_id: crypto.randomUUID(), revision: walletRef.current.revision, ...(demo ? { demo_balance_minor: walletRef.current.balance_minor } : {}) };
    if (action === 'deal') Object.assign(payload, { bet_minor: toMinor(bet), perfect_pairs_minor: mode === 'sidebet' ? toMinor(pairBet) || 0 : 0, twenty_one_three_minor: mode === 'sidebet' ? toMinor(trioBet) || 0 : 0,
      seed_id: fairness.seed_id, nonce: fairness.nonce, client_seed: fairness.client_seed, server_seed_hash: fairness.server_seed_hash });
    else Object.assign(payload, { action, round_id: round.request_id, round_revision: round.round_revision });
    if (action === 'deal') visibleRoundId.current = payload.request_id;
    reserveStake(payload.request_id, action === 'deal' ? (payload.bet_minor || 0) + payload.perfect_pairs_minor + payload.twenty_one_three_minor : round.action_costs?.[action] || 0);
    let presentation = null;
    try {
      effects.prepare();
      sound(action === 'deal' ? 'bet' : 'pick');
      const request = () => axios.post(`${API}/blackjack/${demo ? 'demo/' : ''}${action === 'deal' ? 'deal' : 'action'}`, payload, { headers: gameHeaders(demo) });
      let response;
      try { response = await request(); } catch (failure) { if (failure.response) throw failure; response = await request(); }
      const result = response.data.round;
      if (result.finished) presentation = stageRound(result);
      if (version !== epoch.current) return;
      confirmWallet(payload.request_id, result);
      const next = { ...walletRef.current, balance_minor: response.data.balance_minor,
        revision: demo ? walletRef.current.revision + 1 : response.data.revision,
        rounds: result.finished ? [...walletRef.current.rounds.filter((r) => r.request_id !== result.request_id), result].slice(-50) : walletRef.current.rounds };
      applyWallet(next); if (demo) saveDemoWallet(next);
      const animated = !instant && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      setOpeningStep(action === 'deal' && animated ? 0 : null);
      setSettlementVisible(!result.finished || !animated);
      setDealerShown(result.finished && animated ? 1 : result.dealer_cards.length);
      setRound(result);
      if (action === 'deal' && animated && !await animateOpeningDeal(setOpeningStep, () => version === epoch.current, () => effects.play('card'), pace)) return;
      if ((action === 'deal' && !animated) || ['hit', 'double', 'split'].includes(action)) effects.play('card');
      const nextFairness = await loadFairness(demo);
      if (version === epoch.current) setFairness(nextFairness);
      if (result.finished && animated) {
        for (let visible = 2; visible <= result.dealer_cards.length; visible++) {
          await new Promise((resolve) => setTimeout(resolve, (visible === 2 ? 500 : 420) * pace));
          if (version !== epoch.current) return;
          setDealerShown(visible);
          effects.play('card');
        }
        await new Promise((resolve) => setTimeout(resolve, 260 * pace));
        if (version === epoch.current) setSettlementVisible(true);
      } else if (animated && action !== 'deal') await new Promise((resolve) => setTimeout(resolve, 330 * pace));
      if (version === epoch.current && result.finished) sound(result.payout_minor > result.bet_minor ? 'win' : result.payout_minor < result.bet_minor ? 'loss' : 'hit');
    } catch (failure) {
      cancelStake(payload.request_id);
      if (presentation && version === epoch.current) { setOpeningStep(null); setSettlementVisible(true); setDealerShown(52); }
      if (version === epoch.current) setError(formatApiErrorDetail(failure.response?.data?.detail || 'Connection interrupted. Your saved hand will be restored.'));
    } finally {
      if (presentation) await presentation;
      if (version === epoch.current) { setOpeningStep(null); busyRef.current = false; setBusy(false); refresh(version).catch(() => {}); }
    }
  };
  const rotateSeed = async (clientSeed) => {
    if (active || busyRef.current) throw new Error('Finish your Blackjack hand before rotating seeds.');
    const result = await rotateFairness(demo, fairness, clientSeed); setFairness(result.current); return result;
  };
  const updateGhost = async (ghost) => {
    if (demo || ghostBusy) return;
    setGhostBusy(true);
    try { await axios.put(`${API}/keno/preferences`, { ghost }, { headers: gameHeaders(false) }); applyWallet({ ...walletRef.current, ghost }); }
    catch { setError('Unable to update Ghost mode.'); }
    finally { setGhostBusy(false); }
  };
  const resetDemo = () => { if (demo && !busy && !active) { const value = { balance_minor: 100000, revision: walletRef.current.revision, rounds: [] }; saveDemoWallet(value); applyWallet(value); } };
  useCurrencyBusy(owner, busy || active || openingStep != null || !settlementVisible);
  return { gameType: 'blackjack', access, Verifier: BlackjackVerifyForm, config, wallet: { ...wallet, currency: currency.accountDemo ? 'demo' : 'v' }, displayWallet: { ...presentWallet(wallet), currency: currency.accountDemo ? 'demo' : 'v' }, round, dealerShown, openingStep, settlementVisible, active, fairness, demo, owner, walletLoading: loading || walletLoading || !currency.ready, busy,
    bet, setBet, pairBet, setPairBet, trioBet, setTrioBet, mode, setMode, send, error, instant, setInstant, turbo, setTurbo, muted, setMuted, volume, setVolume,
    ghostBusy, updateGhost, rotateSeed, refreshFairness: async () => setFairness(await loadFairness(demo)), syncWallet: applyWallet, resetDemo,
    auto: false, picking: false, fairnessLocked: active };
};